/**
 * The languages the portal ships with.
 *
 * Every entry carries four things:
 *
 *   - `tag`        the i18next language key and the folder name under `locales/`
 *   - `locale`     the full BCP-47 tag handed to `<I18nProvider>` and `Intl.*`,
 *                  which is what decides date order, first day of week and
 *                  number grouping. `en` alone would give US formatting, so the
 *                  English entry resolves to `en-GB`.
 *   - `endonym`    the language's own name — always shown in the switcher, so a
 *                  user who cannot read the current UI can still find theirs
 *   - `flagCode`   ISO 3166-1 alpha-2 for the design system's `<Flag>` component
 *
 * Adding a language means: a folder under `src/i18n/locales/<tag>/` with all
 * namespace files and an entry here; `resources.ts` lazy-loads the folder.
 */

export interface AppLanguageDescriptor {
  tag: string;
  locale: string;
  endonym: string;
  englishName: string;
  flagCode: string;
}

/**
 * Every language the app has a catalogue for.
 *
 * Declaration order *is* display order — the switcher maps this array — so
 * the sequence (Norican's stated priority) is a business decision, not an
 * alphabetical accident. `tests/locales.test.mjs` holds every catalogue level
 * with English: same keys, same placeholders, the plural forms each language
 * needs.
 *
 * **Everything below the first five is machine-translated** and has not been
 * read by a native speaker; the first five were written by hand. Strings
 * added on 2026-09-24 (simplified incident form, follow-up questions, quick
 * actions) are machine-translated in every language. Expect local IT leads to want wording changed, particularly enum
 * labels where one string serves nouns of different grammatical gender.
 */
export const APP_LANGUAGES = [
  {
    tag: "en",
    locale: "en-GB",
    endonym: "English",
    englishName: "English",
    flagCode: "GB",
  },
  {
    tag: "de",
    locale: "de-DE",
    endonym: "Deutsch",
    englishName: "German",
    flagCode: "DE",
  },
  {
    tag: "da",
    locale: "da-DK",
    endonym: "Dansk",
    englishName: "Danish",
    flagCode: "DK",
  },
  {
    tag: "cs",
    locale: "cs-CZ",
    endonym: "Čeština",
    englishName: "Czech",
    flagCode: "CZ",
  },
  {
    tag: "zh-CN",
    locale: "zh-CN",
    endonym: "简体中文",
    englishName: "Chinese (Simplified)",
    flagCode: "CN",
  },
  {
    tag: "es",
    locale: "es-ES",
    endonym: "Español",
    englishName: "Spanish",
    flagCode: "ES",
  },
  {
    tag: "pl",
    locale: "pl-PL",
    endonym: "Polski",
    englishName: "Polish",
    flagCode: "PL",
  },
  {
    tag: "ja",
    locale: "ja-JP",
    endonym: "日本語",
    englishName: "Japanese",
    flagCode: "JP",
  },
  {
    tag: "it",
    locale: "it-IT",
    endonym: "Italiano",
    englishName: "Italian",
    flagCode: "IT",
  },
  {
    tag: "fr",
    locale: "fr-FR",
    endonym: "Français",
    englishName: "French",
    flagCode: "FR",
  },
  {
    tag: "nl",
    locale: "nl-NL",
    endonym: "Nederlands",
    englishName: "Dutch",
    flagCode: "NL",
  },
  {
    tag: "sv",
    locale: "sv-SE",
    endonym: "Svenska",
    englishName: "Swedish",
    flagCode: "SE",
  },
  {
    tag: "tr",
    locale: "tr-TR",
    endonym: "Türkçe",
    englishName: "Turkish",
    flagCode: "TR",
  },
  {
    tag: "pt-BR",
    locale: "pt-BR",
    endonym: "Português (Brasil)",
    englishName: "Portuguese (Brazil)",
    flagCode: "BR",
  },
  {
    tag: "hi",
    locale: "hi-IN",
    endonym: "हिन्दी",
    englishName: "Hindi",
    flagCode: "IN",
  },
] as const satisfies readonly AppLanguageDescriptor[];

/** Union of the supported i18next language keys, derived from the array above. */
export type AppLanguage = (typeof APP_LANGUAGES)[number]["tag"];

export const APP_LANGUAGE_TAGS = APP_LANGUAGES.map((language) => language.tag) as AppLanguage[];

/**
 * The languages the interface actually offers today — a subset of
 * `APP_LANGUAGES` that can lag it while a new catalogue is being verified.
 * All fifteen are offered (product-owner decision, 2026-09-24).
 */
export const LAUNCH_LANGUAGE_TAGS = APP_LANGUAGE_TAGS;

/** What the language picker renders. A subset of `APP_LANGUAGES`, never a different list. */
export const OFFERED_LANGUAGES = APP_LANGUAGES.filter((language) =>
  (LAUNCH_LANGUAGE_TAGS as readonly string[]).includes(language.tag),
);

/** The language used when detection fails and the fallback for missing keys. */
export const DEFAULT_LANGUAGE: AppLanguage = "en";

/** localStorage key holding an explicit user override. Read by the detector. */
export const LANGUAGE_STORAGE_KEY = "it-helpdesk-agent.language";

export function isAppLanguage(value: string | undefined | null): value is AppLanguage {
  return value != null && APP_LANGUAGE_TAGS.includes(value as AppLanguage);
}

/**
 * Best-effort map from any BCP-47 tag onto one we ship.
 *
 * Exact match wins (`zh-CN`), then the primary subtag (`de-AT` → `de`,
 * `en-US` → `en`), then the two regional tags whose primary subtag we do not
 * ship on its own: Simplified Chinese written as plain `zh`/`zh-Hans`, and
 * Portuguese, where `pt`/`pt-PT` would otherwise fall all the way to English
 * rather than to the Brazilian catalogue sitting right there.
 *
 * Traditional Chinese (`zh-TW`, `zh-Hant`) deliberately does *not* resolve to
 * `zh-CN` — Simplified text is not a courtesy to a Traditional reader — so it
 * falls back to English like anything else we do not ship.
 */
export function resolveAppLanguage(tag: string | undefined | null): AppLanguage {
  if (!tag) {
    return DEFAULT_LANGUAGE;
  }
  if (isAppLanguage(tag)) {
    return tag;
  }
  const lower = tag.toLowerCase();
  if (lower === "zh" || lower.startsWith("zh-hans") || lower.startsWith("zh-cn")) {
    return "zh-CN";
  }
  if (lower === "pt" || lower.startsWith("pt-")) {
    return "pt-BR";
  }
  const primary = lower.split("-")[0];
  if (isAppLanguage(primary)) {
    return primary;
  }
  return DEFAULT_LANGUAGE;
}

export function getLanguageDescriptor(tag: string | undefined | null): AppLanguageDescriptor {
  const resolved = resolveAppLanguage(tag);
  // The find always hits: `resolved` is narrowed to a tag present in the list.
  return APP_LANGUAGES.find((language) => language.tag === resolved) ?? APP_LANGUAGES[0];
}

/** The full BCP-47 locale to hand to `Intl.*` and the design system `I18nProvider`. */
export function getLocale(tag: string | undefined | null): string {
  return getLanguageDescriptor(tag).locale;
}
